"use client";

import React, { useState } from "react";
import { Search, Loader2, User, Phone, Car, Calendar, MapPin, XCircle, FileText, CheckCircle } from "lucide-react";
import Swal from "sweetalert2";

export default function TrackBookingForm() {
  const BOOKING_BASE = "/api/booking/";
  const [bookingCode, setBookingCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [bookingData, setBookingData] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!bookingCode.trim()) {
      Swal.fire({
        title: "خطا",
        text: "لطفاً کد پیگیری را وارد کنید.",
        icon: "warning",
        confirmButtonText: "تایید",
        confirmButtonColor: "#1e3a8a",
      });
      return;
    }

    setIsLoading(true);
    setBookingData(null);
    try {
      const res = await fetch(`${BOOKING_BASE}Booking/GetBooking?BookingCode=${bookingCode}`);
      if (!res.ok) {
        throw new Error("نوبت یافت نشد یا خطایی رخ داده است.");
      }

      const text = await res.text();
      // Handle empty response for invalid codes sometimes
      if (!text) {
        throw new Error("نوبت یافت نشد.");
      }

      const data = JSON.parse(text);
      if (data && data.BookingCode) {
        // Workaround: Fetch real vehicle model using VIN because GetBooking sometimes returns hardcoded incorrect model
        try {
          if (data.BookingVinNumber) {
            const chassisRes = await fetch(`${BOOKING_BASE}Booking/getSubscriberChassisAllocation?VinNumber=${data.BookingVinNumber}`);
            if (chassisRes.ok) {
              const chassisText = await chassisRes.text();
              if (chassisText) {
                const chassisData = JSON.parse(chassisText);
                data.RealVehicleModelName = chassisData.ChassisVehicleModelName || chassisData.ChassisVehicleName;
              }
            }
          }
        } catch (e) {
          console.error("Failed to fetch real vehicle model:", e);
        }

        setBookingData(data);
      } else {
        throw new Error("نوبت یافت نشد.");
      }
    } catch (err) {
      Swal.fire({
        title: "خطا",
        text: err.message || "ارتباط با سرور برقرار نشد.",
        icon: "error",
        confirmButtonColor: "#1e3a8a",
        confirmButtonText: "متوجه شدم"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelBooking = () => {
    Swal.fire({
      title: "لغو نوبت",
      text: "برای لغو نوبت، لطفاً شماره شاسی (VIN) خودرو را وارد کنید:",
      input: "text",
      inputPlaceholder: "شماره شاسی / VIN",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "بله، لغو شود",
      cancelButtonText: "انصراف",
      showLoaderOnConfirm: true,
      preConfirm: async (vinInput) => {
        if (!vinInput || vinInput.trim() === '') {
          Swal.showValidationMessage("لطفاً شماره شاسی را وارد کنید");
          return false;
        }
        if (vinInput.trim().toUpperCase() !== (bookingData.BookingVinNumber || "").toUpperCase()) {
          Swal.showValidationMessage("VIN وارد شده صحیح نمی باشد");
          return false;
        }
        try {
          const res = await fetch(`${BOOKING_BASE}Booking/CancelBooking?BookingCode=${bookingData.BookingCode}&VinNumber=${vinInput.trim()}`);
          if (!res.ok) {
            const errText = await res.text();
            throw new Error(errText || "خطا در ارتباط با سرور");
          }
          return true;
        } catch (error) {
          Swal.showValidationMessage(`خطا: ${error.message}`);
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then((result) => {
      if (result.isConfirmed) {
        const smsText = `مشتری گرامی، نوبت سرویس در محل شما با کد پیگیری ${bookingData.BookingCode} لغو گردید؛ جهت ثبت نوبت جدید میتوانید مجدداً با ما در ارتباط باشید.\nبا تشکر شرکت برساوش تجارت نیک`;
        try {
          fetch("/api/sms/send_sms/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              to: bookingData.BookingMobile,
              message: smsText,
              provider: "faraz"
            })
          }).catch(err => console.error("SMS fetch error:", err));
        } catch (smsError) {
          console.error("SMS sending failed:", smsError);
        }

        Swal.fire({
          title: "موفق",
          text: "نوبت شما با موفقیت لغو شد.",
          icon: "success",
          confirmButtonText: "تایید",
          confirmButtonColor: "#1e3a8a",
        });
        setBookingData(null);
        setBookingCode("");
      }
    });
  };

  const handleViewLocation = () => {
    if (bookingData?.BookingListLatitude && bookingData?.BookingListLongitude) {
      const url = `https://www.google.com/maps/search/?api=1&query=${bookingData.BookingListLatitude},${bookingData.BookingListLongitude}`;
      window.open(url, "_blank");
    } else {
      Swal.fire({
        title: "خطا",
        text: "موقعیت مکانی برای این نوبت ثبت نشده است.",
        icon: "error",
        confirmButtonText: "تایید",
        confirmButtonColor: "#1e3a8a",
      });
    }
  };

  return (
    <div dir="rtl" className="w-full mx-auto">
      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
        <div className="px-5 py-6 sm:px-7 flex flex-col gap-8">

          {/* Header */}
          <div>
            <h3 className="text-xl font-black text-slate-800 mb-2">پیگیری نوبت</h3>
            <p className="text-sm text-slate-500">
              برای مشاهده وضعیت نوبت خود، کد پیگیری پیامک شده را وارد کنید.
            </p>
          </div>

          {/* Search Input */}
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={bookingCode}
                onChange={(e) => setBookingCode(e.target.value)}
                placeholder="مثال: 10-1002-1001"
                dir="ltr"
                className="w-full px-4 py-3 pl-12 rounded-xl border-2 text-sm transition-all outline-none bg-white border-slate-200 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] text-center sm:text-left"
              />
              <FileText className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-large btn-theme-primary sm:w-auto w-full disabled:bg-slate-300 disabled:text-slate-500 disabled:shadow-none disabled:scale-100 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Search size={18} />
              )}
              جستجو
            </button>
          </form>

          {/* Results */}
          {bookingData && (
            <div className="mt-4 border-t border-slate-100 pt-6 flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

              <div className="flex items-center justify-between bg-blue-50 p-4 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 text-blue-800 font-bold">
                  <CheckCircle size={20} className="text-blue-600" />
                  وضعیت نوبت:
                </div>
                <div className="bg-white px-3 py-1 rounded-lg text-sm font-bold text-blue-700 shadow-sm border border-blue-200">
                  {bookingData.BookingStatus}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4">
                <InfoItem
                  icon={<User size={16} />}
                  label="نام و نام خانوادگی"
                  value={`${bookingData.BookingFirstName || ''} ${bookingData.BookingLastName || ''}`}
                />
                <InfoItem
                  icon={<Phone size={16} />}
                  label="شماره موبایل"
                  value={bookingData.BookingMobile}
                />
                <InfoItem
                  icon={<Car size={16} />}
                  label="مدل خودرو"
                  value={bookingData.RealVehicleModelName || bookingData.BookingVehicleModelName || bookingData.BookingVehicleModel}
                />

                <InfoItem
                  icon={<Calendar size={16} />}
                  label="تاریخ و زمان"
                  value={`${bookingData.StrBookingDate} - ساعت ${bookingData.StrBookingTime}`}
                />
                <InfoItem
                  icon={<MapPin size={16} />}
                  label="آدرس"
                  value={bookingData.BookingListFullAddress || 'ثبت نشده'}
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-4 pt-6 border-t border-slate-100">
                <button
                  onClick={handleViewLocation}
                  className="flex-1 btn-large btn-theme-ghost border-2 border-slate-200 text-sm py-3"
                >
                  <MapPin size={18} className="text-[#2b2b2b]" />
                  مشاهده لوکیشن
                </button>
                <button
                  onClick={handleCancelBooking}
                  className="flex-1 btn-large bg-red-50 hover:bg-red-100 text-red-600 border border-red-100 shadow-sm text-sm py-3"
                >
                  <XCircle size={18} />
                  لغو نوبت
                </button>
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
        <span className="text-slate-400">{icon}</span>
        {label}
      </span>
      <span className="text-sm font-bold text-slate-800 pr-5">
        {value || '-'}
      </span>
    </div>
  );
}
